import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, Calendar, Clock, ChevronRight } from 'lucide-react';
import api from '../../services/api';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

const BeneficiairesAPlanifier = () => {
  const navigate = useNavigate();
  const [beneficiaires, setBeneficiaires] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);

  useEffect(() => {
    loadBeneficiaires();
  }, []);

  const loadBeneficiaires = async () => {
    try {
      const response = await api.get('/beneficiaires/a-planifier');
      setBeneficiaires(response.data.beneficiaires.slice(0, 5)); // Top 5 seulement
      setTotal(response.data.total);
    } catch (error) {
      console.error('Erreur chargement bénéficiaires à planifier:', error);
    } finally {
      setLoading(false);
    }
  };

  const getUrgenceConfig = (urgence) => {
    const configs = {
      urgent: {
        bg: 'bg-gradient-to-r from-red-50 to-pink-50',
        border: 'border-red-300',
        badge: 'bg-red-500',
        icon: 'text-red-600',
        label: 'Urgent'
      },
      attention: {
        bg: 'bg-gradient-to-r from-orange-50 to-amber-50',
        border: 'border-orange-300',
        badge: 'bg-orange-500',
        icon: 'text-orange-600',
        label: 'Attention'
      },
      normal: {
        bg: 'bg-gradient-to-r from-blue-50 to-cyan-50',
        border: 'border-blue-200',
        badge: 'bg-blue-500',
        icon: 'text-blue-600',
        label: 'À planifier'
      }
    };
    return configs[urgence] || configs.normal;
  };

  if (loading) {
    return (
      <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-5">
        <div className="animate-pulse space-y-3">
          <div className="h-6 bg-gray-200 rounded w-1/2"></div>
          <div className="h-20 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  if (total === 0) {
    return null; // Ne rien afficher si tout est OK
  }

  return (
    <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
      {/* Header */}
      <div className="bg-gradient-to-r from-orange-500 to-red-500 text-white p-4">
        <div className="flex items-center gap-3">
          <div className="bg-white/20 backdrop-blur-sm rounded-full p-2">
            <AlertTriangle size={24} className="text-white" />
          </div>
          <div className="flex-1">
            <h3 className="font-bold text-lg">⚠️ À planifier cette semaine</h3>
            <p className="text-sm text-orange-100">
              {total} bénéficiaire{total > 1 ? 's' : ''} sans intervention
            </p>
          </div>
        </div>
      </div>

      {/* Liste des bénéficiaires */}
      <div className="divide-y divide-gray-100">
        {beneficiaires.map((benef) => {
          const config = getUrgenceConfig(benef.urgence);
          
          return (
            <div
              key={benef._id}
              onClick={() => navigate('/planning')}
              className={`${config.bg} border-l-4 ${config.border} p-4 hover:shadow-md transition-all cursor-pointer`}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-start gap-3 flex-1">
                  {/* Indicateur urgence */}
                  <div className={`${config.badge} rounded-full w-3 h-3 mt-1.5 animate-pulse`}></div>
                  
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h4 className="font-bold text-gray-800">
                        {benef.prenom} {benef.nom}
                      </h4>
                      <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${config.badge} text-white`}>
                        {config.label}
                      </span>
                    </div>
                    
                    <div className="space-y-1 text-sm text-gray-600">
                      {benef.derniereIntervention ? (
                        <div className="flex items-center gap-2">
                          <Clock size={14} />
                          <span>
                            Dernière intervention : {format(new Date(benef.derniereIntervention), 'd MMM yyyy', { locale: fr })}
                            {benef.joursSansPlanification && (
                              <span className="font-bold text-gray-800 ml-1">
                                (il y a {benef.joursSansPlanification} jours)
                              </span>
                            )}
                          </span>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2 text-red-600 font-medium">
                          <AlertTriangle size={14} />
                          <span>Jamais planifié</span>
                        </div>
                      )}
                      
                      <div className="flex items-center gap-2">
                        <Calendar size={14} />
                        <span>{benef.nombrePlagesHoraires} plage{benef.nombrePlagesHoraires > 1 ? 's' : ''} horaire{benef.nombrePlagesHoraires > 1 ? 's' : ''} définie{benef.nombrePlagesHoraires > 1 ? 's' : ''}</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Bouton action */}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate('/planning');
                  }}
                  className="bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-white font-bold px-4 py-2 rounded-xl flex items-center gap-2 shadow-lg transition-all active:scale-95"
                >
                  <Calendar size={18} />
                  Planifier
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Footer - Voir tout */}
      {total > 5 && (
        <div
          onClick={() => navigate('/gestion?tab=a-planifier')}
          className="bg-gradient-to-r from-gray-50 to-blue-50 p-4 text-center cursor-pointer hover:bg-blue-100 transition-colors border-t border-gray-200"
        >
          <div className="flex items-center justify-center gap-2 text-blue-600 font-bold">
            <span>Voir les {total - 5} autres bénéficiaires</span>
            <ChevronRight size={20} />
          </div>
        </div>
      )}
    </div>
  );
};

export default BeneficiairesAPlanifier;
